import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import React from 'react'

import { AwardIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/AwardIcon'
import { CakeIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CakeIcon'
import { CalendarIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CalendarIcon'
import { HouseIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HouseIcon'
import { UserHeartIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/UserHeartIcon'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { VendorDetails } from '@f-fertighaus/applications/Next/types/vendorResponse'
import { DetailElement } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailElement/DetailElement'

interface GeneralVendorInfoProps {
  vendor: VendorDetails
}

export const GeneralVendorInfo = ({ vendor }: GeneralVendorInfoProps) => {
  const hasInfoField = (field: keyof VendorDetails) => Boolean(vendor[field])

  return (
    <>
      <Title variant='bodyTitle' component='p'>
        {t('houseDetailPage.vendor.vendor-in-numbers', {
          vendorName: vendor.display_name
        })}
      </Title>
      <Wrapper>
        {hasInfoField('year_of_foundation') && (
          <DetailElement
            verticalLayout
            icon={CakeIcon}
            name={t('houseDetailPage.vendor.founding-year')}>
            <Typography variant='body2'>{vendor.year_of_foundation}</Typography>
          </DetailElement>
        )}
        {hasInfoField('number_of_houses') && (
          <DetailElement
            verticalLayout
            icon={HouseIcon}
            name={t('houseDetailPage.vendor.houses-on-offer')}>
            <Typography variant='body2'>{vendor.number_of_houses}</Typography>
          </DetailElement>
        )}
        {hasInfoField('houses_constructed_per_year') && (
          <DetailElement
            verticalLayout
            icon={CalendarIcon}
            name={t('houseDetailPage.vendor.houses-per-year')}>
            <Typography variant='body2'>
              {vendor.houses_constructed_per_year}
            </Typography>
          </DetailElement>
        )}
        {hasInfoField('houses_constructed_total') && (
          <DetailElement
            verticalLayout
            icon={AwardIcon}
            name={t('houseDetailPage.vendor.houses-total')}>
            <Typography variant='body2'>
              {vendor.houses_constructed_total}
            </Typography>
          </DetailElement>
        )}
        {hasInfoField('number_of_employees') && (
          <DetailElement
            verticalLayout
            icon={UserHeartIcon}
            name={t('houseDetailPage.vendor.employees')}>
            <Typography variant='body2'>
              {vendor.number_of_employees}
            </Typography>
          </DetailElement>
        )}
      </Wrapper>
    </>
  )
}

const Wrapper = styled.ul`
  margin-block-start: 0;
  margin-block-end: 0;
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-inline-start: 0;
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-wrap: wrap;
    background: ${({ theme }) => theme.palette.glass[500]};
    padding-top: 16px;
    padding-right: 16px;

    & > li {
      margin-left: ${({ theme }) => theme.spacing(2)};
    }
  }
`

const Title = styled(Typography)`
  margin-top: 32px;
  margin-bottom: 16px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: 24px;
    margin-bottom: 8px;
  }
`
